import { Injectable } from '@angular/core';
import { AppConfigService } from '../../app-config.service';
import { TypeProjet } from '../constants/type-projet.enum';
import { FormatPhoneNumberHelper } from '../helpers/format-phone-number-helper';
import { ProvinceEnum } from '../constants/province.enum';

export interface DepenseDonneeReference {
  label: string;
  id: string;
  obligatoire: boolean;
  ordreAffichage: number;
}

export interface FraisEstimesReferences {
  taxesScolaires: number;
  taxesMunicipales: number;
  coutsEnergie: number;
  fraisCopropriete: number;
}

export interface ProduitsReference {
  label: string;
  id: string;
  taux: number;
  defaut: string;
  caracteristiques: string;
  noteLegale: string;
}

export interface AmortissementReference {
  label: string;
  id: string;
  defaut: string;
}

export interface PeriodeAchatReference {
  label: string;
  code: string;
  ordreAffichage: number;
}

export interface PeriodeRappelReference {
  label: string;
  code: string;
  ordreAffichage: number;
}

export interface SecteurEmploiReference {
  label: string;
  id: string;
}

export interface TypeEmploiReference {
  label: string;
  id: string;
}

export interface StatutResidenceReference {
  label: string;
  id: string;
}

export interface TextConsent {
  textLong: string;
  textShort: string;
  id: string;
  language: string;
  province: ProvinceEnum;
}

export interface DemandeSupportReference {
  sansFrais: string;
  montreal: string;
  resultatCapSansFrais: string;
  resultatCapMontreal: string;
  assurancePretSansFrais: string;
  assuranceHabitationSansFrais: string;
}

const INSTITUTION_ONTARIO = '829';

@Injectable()
export class DonneReferenceService {
  typeProprieteDonneReference: Array<Object> = [];
  typePropriete: Array<string> = [];

  typeFrequence: Array<{ label: string; id: string }>;
  typeDepenses: Array<DepenseDonneeReference>;

  typePrduit: ProduitsReference[] = [];
  typeAmortissement: AmortissementReference[] = [];
  tauxInteret: string;

  periodesAchat: PeriodeAchatReference[] = [];
  periodesRappel: PeriodeRappelReference[] = [];

  numerosDemandeSupport: DemandeSupportReference;

  secteurEmploi: SecteurEmploiReference[] = [];
  typeEmploi: TypeEmploiReference[] = [];
  statutResidence: StatutResidenceReference[] = [];

  textConsentMPM: TextConsent[] = [];
  textConsentExperimente: TextConsent[] = [];

  constructor(private readonly _appConfigService: AppConfigService) {
    this.setTypePropriete();
    this.setTypeFrequence();
    this.setTypeDepenses();
    this.setTypeFraisEstimes();
    this.setTypeAmortissement();
    this.setTypeProduit();
    this.setTypePeriodeAchat();
    this.setTypePeriodeRappel();
    this.setNumerosDemandeSupport();
    this.setSecteurEmploi();
    this.setTypeEmploi();
    this.setStatutResident();
    this.setTextConsentMPM();
    this.setTextConsentExperimente();
  }

  setTypeProduit() {
    const produit = this._appConfigService.donneReference['PRODUIT.TERME'];
    const langue = this._appConfigService.langue;
    const caracteristiques = `caracteristiques${this.capitalizeFirstLetter(langue)}`;
    const noteLegale = `noteLegale${this.capitalizeFirstLetter(langue)}`;

    for (const key of Object.keys(produit)) {
      const label = produit[key]['description'][langue];
      const valeur = produit[key]['description']['valeur'];
      const defaut = produit[key]['description']['defaut'];
      const caracteristique = produit[key]['description'][caracteristiques];
      const note = produit[key]['description'][noteLegale];
      this.typePrduit.push({
        label,
        taux: valeur,
        id: key,
        defaut,
        caracteristiques: caracteristique,
        noteLegale: note
      });
    }
  }

  setTypeAmortissement() {
    const amortissements = this._appConfigService.donneReference['AMORTISSEMENT'];
    const langue = this._appConfigService.langue;

    for (const key of Object.keys(amortissements)) {
      const label = amortissements[key]['description'][langue];
      const defaut = amortissements[key]['description']['defaut'];
      this.typeAmortissement.push({ label, id: key, defaut });
    }
  }

  setTypeFraisEstimes() {
    const frais = this._appConfigService.donneReference['FRAIS.ESTIME'];
    this.tauxInteret = frais['tauxInteret']['description']['valeur'];
  }

  setTypeDepenses() {
    const depenses = this._appConfigService.donneReference['DEPENSE.MENSUEL'];

    const langue = this._appConfigService.langue;

    const typeDepenses: Array<DepenseDonneeReference> = [];

    for (const key of Object.keys(depenses)) {
      const desc = depenses[key]['description'][langue];
      const obligatoireString = depenses[key]['description']['requis'];
      const obligatoire = obligatoireString === 'O' ? true : false;
      const ordreAffichage = Number.parseInt(depenses[key]['description']['ordreAffichage']);

      if (desc) {
        typeDepenses.push({ label: desc, id: key, obligatoire, ordreAffichage });
      }
    }

    this.typeDepenses = typeDepenses;
  }

  getTypeDepense(depenseId: string): DepenseDonneeReference {
    return this.typeDepenses.find(depense => depenseId === depense.id);
  }

  setTypeFrequence() {
    const frequences = this._appConfigService.donneReference['FREQUENCE.REVENU_NET'];

    const langue = this._appConfigService.langue;

    const typeFrequence: Array<{ label: string; id: string }> = [];

    for (const key of Object.keys(frequences)) {
      const desc = frequences[key]['description'][langue];

      if (desc) {
        typeFrequence.push({ label: desc, id: key });
      }
    }
    typeFrequence.sort((a, b) => {
      return frequences[a.id].ordreAffichage - frequences[b.id].ordreAffichage;
    });

    this.typeFrequence = typeFrequence;
  }

  setTypePropriete(): void {
    const typeDonneReference = this._appConfigService.donneReference['PROPRIETE.TYPE'];

    for (const value of Object.keys(typeDonneReference)) {
      // Rebuild object for UI usecase
      const typeProprieteeUI = {
        id: value,
        typeProprietee:
          typeDonneReference[value]['description'][this._appConfigService.configData.contexteEnvironnement.langue]
      };

      // Array for later use to compare ids to send to back-end
      this.typeProprieteDonneReference.push(typeProprieteeUI);

      // Variable population for UI list only
      this.typePropriete.push(typeProprieteeUI['typeProprietee']);
    }
  }

  setTypePeriodeAchat(): void {
    const periodes = this._appConfigService.donneReference['PERIODE.ACHAT'];
    const langue = this._appConfigService.configData.contexteEnvironnement.langue;

    for (const codePeriode of Object.keys(periodes)) {
      this.periodesAchat.push({
        code: codePeriode,
        label: periodes[codePeriode]['description'][langue],
        ordreAffichage: Number.parseInt(periodes[codePeriode]['description']['ordreAffichage'])
      });
    }
  }

  setTypePeriodeRappel(): void {
    const periodes = this._appConfigService.donneReference['PERIODE.RAPPEL'];
    const langue = this._appConfigService.configData.contexteEnvironnement.langue;

    for (const codePeriode of Object.keys(periodes)) {
      this.periodesRappel.push({
        code: codePeriode,
        label: periodes[codePeriode]['description'][langue],
        ordreAffichage: Number.parseInt(periodes[codePeriode]['description']['ordreAffichage'])
      });
    }
  }

  // To be able to search through list of property types based on ID or direct string value
  // Returns a custom object of the type of property with ID and string value
  getTypeProprieteeSelected(propertyIdentifier: string, typeSelection: string): Object {
    if (typeSelection === 'propertyType') {
      return this.typeProprieteDonneReference.find(typeProprieteeUI => {
        return typeProprieteeUI['typeProprietee'] === propertyIdentifier;
      });
    } else if (typeSelection === 'propertyID') {
      return this.typeProprieteDonneReference.find(typeProprieteeUI => {
        return typeProprieteeUI['id'] === propertyIdentifier;
      });
    }

    // This should never be reached but has been added for SonarQube error trapping.
    return null;
  }

  getTypeTaux(idTaux: string): ProduitsReference {
    return this.typePrduit.find(taux => idTaux === taux.id);
  }

  getCaracteristiquesTaux(idTaux: string): string {
    return this.getTypeTaux(idTaux).caracteristiques;
  }

  getTypeAmortissement(idAmortissement: string): AmortissementReference {
    return this.typeAmortissement.find(amortissement => idAmortissement === amortissement.id);
  }

  getCurrentReferentialTypeAmortissement(): AmortissementReference {
    return this.typeAmortissement.find(amortissement => 'O' === amortissement.defaut.toUpperCase());
  }

  getCurrentReferentialProduit(): ProduitsReference {
    return this.typePrduit.find(produit => 'O' === produit.defaut.toUpperCase());
  }

  getTypeFrequence(codeFrequence: string) {
    return this.typeFrequence.find(frequence => codeFrequence === frequence.id);
  }

  setNumerosDemandeSupport() {
    const numbers = this._appConfigService.donneReference['DEMANDE.SUPPORT'];
    const language = this._appConfigService.langue;

    this.numerosDemandeSupport = {
      sansFrais: FormatPhoneNumberHelper.formatNumberPhone(numbers['sansFrais']['description'][language], language),
      montreal: FormatPhoneNumberHelper.formatNumberPhone(numbers['montreal']['description'][language], language),

      resultatCapSansFrais: FormatPhoneNumberHelper.formatNumberPhone(
        numbers['resultatCapSansFrais']['description'][language],
        language
      ),
      resultatCapMontreal: FormatPhoneNumberHelper.formatNumberPhone(
        numbers['resultatCapMontreal']['description'][language],
        language
      ),
      assurancePretSansFrais: FormatPhoneNumberHelper.formatNumberPhone(
        numbers['assurancePretSansFrais']['description'][language],
        language
      ),
      assuranceHabitationSansFrais: FormatPhoneNumberHelper.formatNumberPhone(
        numbers['assuranceHabitationSansFrais']['description'][language],
        language
      )
    };
  }

  setSecteurEmploi(): void {
    const secteursEmploi = this._appConfigService.donneReference['SECTEUR.EMPLOI'];
    const langue = this._appConfigService.configData.contexteEnvironnement.langue;

    for (const idSecteur of Object.keys(secteursEmploi)) {
      this.secteurEmploi.push({
        label: secteursEmploi[idSecteur]['description'][langue],
        id: idSecteur
      });
    }

    this.secteurEmploi.sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
  }

  setTypeEmploi(): void {
    const statutsEmploi = this._appConfigService.donneReference['TYPE.EMPLOI'];
    const langue = this._appConfigService.configData.contexteEnvironnement.langue;

    for (const idStatutsEmploi of Object.keys(statutsEmploi)) {
      this.typeEmploi.push({
        id: idStatutsEmploi,
        label: statutsEmploi[idStatutsEmploi]['description'][langue]
      });
    }
  }

  setStatutResident(): void {
    const statutResidence = this._appConfigService.donneReference['STATUS.LOGEMENT'];
    const langue = this._appConfigService.configData.contexteEnvironnement.langue;

    for (const idStatutResidence of Object.keys(statutResidence)) {
      this.statutResidence.push({
        id: idStatutResidence,
        label: statutResidence[idStatutResidence]['description'][langue]
      });
    }
  }

  setTextConsentMPM(): void {
    this.textConsentMPM = this.parseTextConsent('TEXTE.CONSENTEMENT');
  }

  setTextConsentExperimente(): void {
    this.textConsentExperimente = this.parseTextConsent('TEXTE.CONSENTEMENT_MEMBRE_EXPERIMENTE');
  }

  private parseTextConsent(donneReferenceKey: string): TextConsent[] {
    const listTextConsent = this._appConfigService.donneReference[donneReferenceKey];
    const textConsent = [];

    for (const key of Object.keys(listTextConsent)) {
      const id = key;

      ['fr', 'en'].forEach(language => {
        let textLong = null;
        let textShort = null;
        if (listTextConsent[key]['description'][language]) {
          textLong = listTextConsent[key]['description'][language];
          if (listTextConsent[key]['descriptionCourte'] && listTextConsent[key]['descriptionCourte'][language]) {
            textShort = listTextConsent[key]['descriptionCourte'][language];
          }
          const province =
            listTextConsent[key]['institution'] === INSTITUTION_ONTARIO ? ProvinceEnum.Ontario : ProvinceEnum.Quebec;
          textConsent.push({ textLong, textShort, id, language, province });
        }
      });
    }

    return textConsent;
  }

  getFraisProprieteMoyenne(): number {
    return 0; // TODO à retirer avec le retrait des modules de capacité et préautorisation N3D2
  }

  getConsentText(typeProjet?: TypeProjet): TextConsent {
    const textConsent =
      typeProjet === TypeProjet.ExperimenteRemplacer || typeProjet === TypeProjet.ExperimenteSecondaire
        ? this.textConsentExperimente
        : this.textConsentMPM;

    for (const text of textConsent) {
      if (text.language === this._appConfigService.langue && text.province === this._appConfigService.province) {
        return text;
      }
    }
    return null;
  }

  private capitalizeFirstLetter(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
}
